import React from 'react';

import {useCurrentUserQuery, usePublicEventsQuery} from '@/api';

import {EventCard} from '../components/EventCard';
import {GoogleMapView} from '../components/GoogleMapView';
import {OutlinePrimaryLink} from '../components/OutlinePrimaryLink';
import {SwiperCarousel} from '../components/SwiperCarousel';

const WORKS_ITEMS = [
  {
    image: '/assets/works/works01.jpg',
    tag: 'イベント',
    description:
      '「親子でプログラミング」会場として、親子でLEDを自由に光らせたりロボットを目的地に動かしたりプログラミングを体験する２日間のイベントにてご利用。（スペース春）',
    organizerName: 'ロボット・ドリーム研究会',
    organizerHref: 'https://www.facebook.com/events/1759120681057583/',
  },
  {
    image: '/assets/works/works02.png',
    tag: 'フォーラム',
    description:
      '｢人工知能が普及する時代に、どのような教育が求められるか？｣｢主体的・対話的で深い学び(新学習指導要領)とは、どういうことか？｣｢2020年小学校でのプログラミング教育必修化は、なぜ必要なのか？｣これらの問いをともに考えるフォーラム「地域づくりは人づくり〜これからの教育を考えよう〜」会場としてご利用。（スペース春）',
    organizerName: '（公財）松下政経塾',
    organizerHref: 'https://www.facebook.com/events/981196312029127/',
  },
  {
    image: '/assets/works/works03.png',
    tag: '上映会',
    description:
      '神田昌典氏の講演会を特別にACMで「２０２２講演 上映会」としてご利用。全５回実施。（スペース春）',
    organizerName: '株式会社エムセック',
    organizerHref: 'https://www.facebook.com/events/1895693060455507/',
  },
  {
    image: '/assets/works/works04.jpg',
    tag: 'ワークショップ',
    description:
      '三原市を『水際生活都市』として地域活性化するための「市中心部のグランドデザイン」ワークショップ会場としてご利用。（スペース春＋夏＋秋＋冬）',
    organizerName: '三原市 経営企画課',
    organizerHref:
      'http://www.city.mihara.hiroshima.jp/soshiki/6/grand-design.html',
  },
  {
    image: '/assets/works/works05.jpg',
    tag: 'パーティー',
    description:
      '三原の名物といえば、スイーツ。そんなスイーツを食べながら、素敵な相手と巡り合うことができる「三原スイーツde婚活パーティー」会場として（スペース春＋夏＋秋）',
    organizerName: '三原市 地域調整課',
    organizerHref: 'https://www.facebook.com/events/138539740202392/',
  },
];

export const TopPage: React.FC = () => {
  const {data: currentUserData} = useCurrentUserQuery();
  const {data: eventsData} = usePublicEventsQuery();
  const currentUser = currentUserData?.currentUser;
  const events = eventsData?.publicEvents ?? [];

  return (
    <main>
      {/* Hero */}
      <div className="relative isolate flex h-[540px] min-h-[480px] items-center justify-center overflow-hidden bg-gray-900">
        <div
          className="absolute inset-0 -z-10 bg-cover bg-center blur-sm"
          style={{backgroundImage: "url('/assets/top-hero.jpg')"}}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 -z-10 bg-black/50"
          aria-hidden="true"
        />
        <div className="mx-auto max-w-screen-xl px-4 py-20 text-center text-white">
          <p className="mb-4 font-hand text-[60px] leading-tight tracking-tight lg:text-[123px]">
            Fun Meet!!
          </p>
          <p className="mb-8 text-lg font-extralight lg:text-3xl">
            ミハラの街で「で・あ・う」を楽しもう。つながろう。
          </p>
          {!currentUser && (
            <>
              <OutlinePrimaryLink href="/signup" variant="filled">
                無料で新規登録
              </OutlinePrimaryLink>
              <p className="mt-5 text-sm">
                <span className="mr-4 opacity-50">または</span>
                <a
                  className="font-semibold text-white hover:underline"
                  href="/signin">
                  ログイン
                </a>
              </p>
            </>
          )}
        </div>
      </div>

      {/* Features intro */}
      <section className="pb-28 pt-32">
        <div className="mx-auto px-4 text-center">
          <h2 className="text-2xl font-light leading-normal md:text-3xl">
            「で・あ・う」でより楽しく！
          </h2>
          <p className="mt-12 text-lg leading-relaxed text-gray-600">
            広島県三原市には様々な背景を持った人がいます。
            <br />
            そんな人たちが同じ目的のもと、実際に集まると何かすごいことが起こるはず。
            <br />
            趣味がビジネスになったり、ありえないアイデアが生まれたり、偶然の出会いが一生の仲間になることもある。
            <br />
            ACTION CENTER
            MIHARA（ACミハラ）は、顔を合わせて集い合い、楽しいコミュニティーをつくり、
            <br />
            人生がもっと楽しくなるお手伝いをします。
          </p>
          <OutlinePrimaryLink href="/about" className="mt-12">
            ACミハラについて
          </OutlinePrimaryLink>
        </div>
      </section>

      {/* Gallery */}
      <section>
        <img
          src="/assets/top-gallery.jpg"
          alt=""
          className="h-auto w-full object-cover"
        />
      </section>

      {/* Feature sections */}
      <div className="py-16 md:pb-24 md:pt-20">
        <div className="mx-auto max-w-screen-lg space-y-9 px-4">
          {/* feature-01 */}
          <section className="flex flex-col items-center md:flex-row">
            <div className="w-full md:w-1/2 md:pr-6">
              <img src="/assets/feature-01@2x.png" alt="" className="w-full" />
            </div>
            <div className="w-full pt-4 md:w-1/2 md:pl-6 md:pr-12 md:pt-3">
              <h2 className="text-md mb-8 inline-flex border-b-2 border-primary pb-1 font-semibold text-primary">
                イベントやグループに参加
              </h2>
              <p className="mb-5 text-2xl font-light leading-normal md:text-3xl">
                あなたの&rdquo;好きなこと&rdquo;に
                <br />
                きっと出会える。
              </p>
              <p className="leading-relaxed text-gray-600">
                三原市で活動しているグループや開催されるイベントの中から、興味があることを見つけて参加してみよう。好きなことに出会ったら、ACミハラでもっとやろう。
              </p>
            </div>
          </section>

          {/* feature-02 (image right) */}
          <section className="flex flex-col items-center md:flex-row">
            <div className="w-full md:order-2 md:w-1/2 md:pl-6">
              <img src="/assets/feature-02@2x.png" alt="" className="w-full" />
            </div>
            <div className="w-full pt-4 md:order-1 md:w-1/2 md:pl-12 md:pr-6 md:pt-3">
              <h2 className="text-md mb-8 inline-flex border-b-2 border-primary pb-1 font-semibold text-primary">
                イベントやグループをつくる
              </h2>
              <p className="mb-5 text-2xl font-light leading-normal md:text-3xl">
                さぁ、ファンをつくろう。
              </p>
              <p className="leading-relaxed text-gray-600">
                あなたが好きなこと、伝えたいこと、広めたいことはなんですか？
                {'　'}
                ACミハラでグループやイベントを作って、登録メンバーから仲間を見つけよう。ACミハラなら誰でも簡単にグループやイベントを作成・管理ができます。
              </p>
            </div>
          </section>

          {/* feature-03 */}
          <section className="flex flex-col items-center md:flex-row">
            <div className="w-full md:w-1/2 md:pr-6">
              <img src="/assets/feature-03@2x.png" alt="" className="w-full" />
            </div>
            <div className="w-full pt-4 md:w-1/2 md:pl-6 md:pr-12 md:pt-3">
              <h2 className="text-md mb-8 inline-flex border-b-2 border-primary pb-1 font-semibold text-primary">
                スペースをかりる
              </h2>
              <p className="mb-5 text-2xl font-light leading-normal md:text-3xl">
                用途はさまざま。使い方はあなた次第。
              </p>
              <p className="leading-relaxed text-gray-600">
                ACミハラには様々な人数・種類のイベントに対応できる設備とスペースがあります。セミナーやイベントスペースとして用途に応じてお好きな形にレイアウト変更が可能です。また、イベントの開催がない時間はワークスペースとして利用できます。仕事や勉強で集中したい時、打ち合わせなどグループで集まりたい時にはACミハラをご利用ください。
              </p>
            </div>
          </section>
        </div>
      </div>

      {/* Call to action - join */}
      <section className="relative isolate flex min-h-80 items-center justify-center overflow-hidden py-16 md:pb-24 md:pt-20">
        <div
          className="absolute inset-0 -z-10 bg-cover bg-center"
          style={{backgroundImage: "url('/assets/photo-1.jpg')"}}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 -z-10 bg-black/60"
          aria-hidden="true"
        />
        <div className="mx-auto px-4 text-center text-white">
          <h2 className="text-2xl font-light leading-normal md:text-3xl">
            {currentUser
              ? 'ACミハラに参加しよう。'
              : '今すぐ会員登録して、ACミハラに参加しよう。'}
          </h2>
          {!currentUser && (
            <OutlinePrimaryLink
              href="/signup"
              variant="filled"
              className="mt-8">
              無料で新規登録
            </OutlinePrimaryLink>
          )}
        </div>
      </section>

      {/* Events carousel */}
      <section id="events" className="py-16 md:pb-24 md:pt-20">
        <div className="mx-auto mb-8 max-w-screen-lg px-4 text-center">
          <h2 className="mb-8 inline-flex border-b-2 border-primary pb-1 text-lg font-semibold text-primary">
            おすすめイベント
          </h2>
          <p className="text-2xl font-light leading-normal md:text-3xl">
            イベントに参加して、
            <br />
            あなたの人生が変わるような出会いを見つけよう。
          </p>
        </div>
        <div className="mx-auto max-w-screen-lg px-4">
          {events.length > 0 ? (
            <SwiperCarousel
              slidesPerView={1}
              spaceBetween={32}
              breakpoints={{
                767: {slidesPerView: 3, slidesPerGroup: 3, spaceBetween: 40},
              }}>
              {events
                .filter((event): event is NonNullable<typeof event> => !!event)
                .map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
            </SwiperCarousel>
          ) : (
            <p className="text-center text-gray-500">
              現在開催予定のイベントはありません。
            </p>
          )}
        </div>
      </section>

      {/* Call to action - create event */}
      <section className="relative isolate flex min-h-80 items-center justify-center overflow-hidden py-16 md:py-32">
        <div
          className="absolute inset-0 -z-10 bg-cover bg-center"
          style={{backgroundImage: "url('/assets/photo-2.jpg')"}}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 -z-10 bg-black/40"
          aria-hidden="true"
        />
        <div className="mx-auto max-w-3xl px-4 text-center text-white">
          <h2 className="mb-12 text-2xl font-light leading-normal md:text-3xl">
            あなたが行動すれば、誰かが出会う。
            <br />
            イベントを作ってみませんか？
          </h2>
          <p className="mb-8 text-lg leading-relaxed">
            あなたの思いを表現すれば、
            <br />
            その思いに共感し、協力・応援してくれる仲間がきっと見つかる！
            <br />
            ACミハラはそんなあなたの活動を全力でサポートします。
          </p>
          <OutlinePrimaryLink
            href="/mypage/events/new?eventType=event"
            variant="filled">
            イベントをつくる
          </OutlinePrimaryLink>
          <p className="mt-5 text-lg">
            <a
              className="font-semibold text-white hover:text-primary"
              href="/mypage/events/new?eventType=place">
              スペースをかりる
            </a>
          </p>
        </div>
      </section>

      {/* Works / case studies */}
      <section
        id="works"
        className="overflow-hidden bg-neutral-100 py-16 md:pb-24 md:pt-20">
        <div className="mx-auto mb-8 max-w-screen-lg px-4 text-center">
          <h2 className="mb-8 inline-flex border-b-2 border-primary pb-1 text-lg font-semibold text-primary">
            活用事例
          </h2>
          <p className="text-2xl font-light leading-normal md:text-3xl">
            ACミハラを様々な目的で活用している人が増えています。
            <br />
            さあ、あなたも。
          </p>
        </div>
        <div className="works-carousel">
          <div className="mx-auto max-w-3xl px-4">
            <SwiperCarousel
              centeredSlides
              overflowVisible
              slidesPerView={1}
              spaceBetween={32}
              breakpoints={{767: {centeredSlides: true, spaceBetween: 100}}}>
              {WORKS_ITEMS.map((item) => (
                <article
                  key={item.image}
                  className="flex h-80 w-[720px] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm md:flex-row">
                  <div className="md:flex-1">
                    <img
                      src={item.image}
                      alt=""
                      className="aspect-[4/3] size-full object-cover"
                    />
                  </div>
                  <div className="flex flex-col p-5 md:flex-1">
                    <span className="mb-3 inline-block w-fit rounded-full bg-primary px-3 py-1 text-xs font-semibold text-white">
                      {item.tag}
                    </span>
                    <p className="mb-auto text-sm leading-relaxed text-gray-700">
                      {item.description}
                    </p>
                    <div className="mt-4">
                      <div className="text-xs text-gray-500">主催者</div>
                      <a
                        href={item.organizerHref}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-primary hover:underline">
                        {item.organizerName}
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </SwiperCarousel>
          </div>
        </div>
      </section>

      {/* Call to action - rent space */}
      <section className="relative isolate flex min-h-80 items-center justify-center overflow-hidden py-16 md:pb-28 md:pt-32">
        <div
          className="absolute inset-0 -z-10 bg-cover bg-center"
          style={{backgroundImage: "url('/assets/space.jpg')"}}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 -z-10 bg-black/40"
          aria-hidden="true"
        />
        <div className="mx-auto max-w-3xl px-4 text-center text-white">
          <h2 className="mb-10 text-2xl font-light leading-normal md:text-3xl">
            スペースを利用しよう！
          </h2>
          <p className="mb-10 text-lg leading-relaxed">
            ACミハラの新しくきれいなスペースと、
            <br />
            無料設備でおまちしています。
          </p>
          <OutlinePrimaryLink
            href="/mypage/events/new?eventType=place"
            variant="filled">
            スペースをかりる
          </OutlinePrimaryLink>
        </div>
      </section>

      {/* Access */}
      <section className="grid h-[480px] grid-cols-2">
        <div className="flex flex-col items-center justify-center px-8 py-16 text-left">
          <h2 className="mb-8 inline-flex border-b-2 border-primary pb-1 text-xl font-semibold text-primary">
            アクセス
          </h2>
          <p className="mb-1 text-xl leading-relaxed">
            〒723-0014 広島県三原市城町1-12-5
            <br />
            エムセックビル1〜2階
          </p>
          <p className="mb-8 text-lg text-gray-500">
            JR三原駅より徒歩5分。三原港より徒歩3分。
          </p>
          <OutlinePrimaryLink
            href="https://www.google.com/maps/search/?api=1&query=%E5%BA%83%E5%B3%B6%E7%9C%8C%E4%B8%89%E5%8E%9F%E5%B8%82%E5%9F%8E%E7%94%BA1-12-5"
            target="_blank"
            rel="noreferrer">
            Google Map で見る
          </OutlinePrimaryLink>
        </div>
        <div>
          <GoogleMapView />
        </div>
      </section>
    </main>
  );
};
